import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Text, View, TouchableOpacity, Pressable, Linking } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Input from '@/components/General/inputs/Input';
import SelectInput from '@/components/General/inputs/SelectInput';
import Checkbox from '@/components/General/inputs/Checkbox';
import DatePickerInput from '@/components/General/inputs/DatePickerInput'
import SheetSelector from '@/components/General/inputs/SheetSelector';
import InputSection from '@/components/General/inputs/InputSection';
import { useQueryClient } from '@tanstack/react-query';
import { Mutation } from '@/utils/react-query/Mutations';
import CustomField from '@/components/CustomFields/CustomField'
import * as Validator from "@/utils/InputValidator"
import { Toast } from 'react-native-toast-message/lib/src/Toast';
import { Query } from '@/utils/react-query/Queries';
import { getPersonProfile } from '@/data/people';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import usePlatform from '@/utils/hooks/usePlatform';
import ProfileCard from '@/components/People/ProfileCard';
import FamilyMemberRow, { FamilyPositionTrigger } from '@/components/People/FamilyMemberRow';
import SolidButton from '@/components/General/SolidButton';
import {useEditScreenStore} from '@/data/store/useEditScreenStore';
import { redirectToDefault } from '@/utils/constants/linking';
import { useMutation } from '@tanstack/react-query';
import { saveUserSettings } from '@/data/settings';
import { useLocalSearchParams, useRouter, usePathname } from 'expo-router';
import { sendOptIn } from '@/data/sms';
import DropdownMenu from '@/components/General/DropdownMenu';
import ModalWrapper from '@/components/General/ModalWrapper';
import { TabView } from 'react-native-tab-view';
import Unicon from '@/components/General/Unicon';
import ReachTabBar from '@/components/General/ReachTabBar';
import SheetModal from '@/components/General/SheetModal';
import AddPersonQuickForm from '@/components/People/AddPersonQuickForm';
import DuplicateReviewModal from '@/components/People/DuplicateReviewModal';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg } from '@/utils/organizationEntitlements';

// Keys that must never be sent to savePerson when a user without 'Edit People'
// edits their own profile — the backend self-edit trigger only allows a
// whitelisted set of columns to change.
const SELF_EDIT_BLOCKED_KEYS = ['Family', 'Roles', 'customFieldValues', 'invitedByID', 'invitedByNote', 'isArchived', 'isAdmin', 'familyID', 'familyRole', 'breezePersonID', 'barcode', 'breezeAvatarURL', 'organizationID', 'InvitedBy'];

const EMPTY_DRAFT = {};

export default function AddPersonModal() {
    const [ index, setIndex ] = useState(0);
    const [ firstRender, setFirstRender ] = useState(true)
    const [ showQuickAddFamily, setShowQuickAddFamily ] = useState(false)
    const [ familyMode, setFamilyMode ] = useState(null) // null, 'create', 'join'
    const { id, from, fromScreen, fromStoreKey, batchIds } = useLocalSearchParams()
    const batchIdList = useMemo(() => {
        if(!batchIds) return null;
        try {
            const parsed = JSON.parse(batchIds);
            return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
        } catch {
            return null;
        }
    }, [batchIds]);
    const isBatch = !!batchIdList && !id;
    const storeKey = isBatch ? "batch" : (id || "new")

    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const personDetails = useEditScreenStore(s => s.AddPersonModal?.[storeKey]) || EMPTY_DRAFT

    // Duplicate detection — only for new (non-batch) people. Debounce the
    // entered name, then look up existing people with the same first + last
    // name (case-insensitive). The warning is dismissible per name pair.
    const [debouncedFirstName, setDebouncedFirstName] = useState('');
    const [debouncedLastName, setDebouncedLastName] = useState('');
    const [ignoredDuplicateKey, setIgnoredDuplicateKey] = useState(null);
    const [reviewPersonId, setReviewPersonId] = useState(null);
    const canCheckDuplicates = !id && !isBatch;

    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedFirstName(personDetails.firstName?.trim() || '');
            setDebouncedLastName(personDetails.lastName?.trim() || '');
        }, 400);
        return () => clearTimeout(t);
    }, [personDetails.firstName, personDetails.lastName]);

    const duplicateKey = debouncedFirstName && debouncedLastName
        ? `${debouncedFirstName.toLowerCase()}|${debouncedLastName.toLowerCase()}`
        : null;

    const { data: potentialDuplicates } = Query.people.byName({
        firstName: debouncedFirstName,
        lastName: debouncedLastName,
        options: { enabled: canCheckDuplicates && !!duplicateKey },
    });

    const visibleDuplicates = useMemo(() => {
        if(!canCheckDuplicates || !potentialDuplicates?.length) return [];
        if(ignoredDuplicateKey === duplicateKey) return [];
        return potentialDuplicates;
    }, [canCheckDuplicates, potentialDuplicates, ignoredDuplicateKey, duplicateKey]);

    const renderTabBar = useMemo(() => props => <ReachTabBar {...props} scrollEnabled />, []);
    const queryClient = useQueryClient()
    const { isNative } = usePlatform();
    const router = useRouter();
    const pathname = usePathname();

    const { entitlements } = useOrganizationEntitlements();
    // Custom fields are paid-only — Free orgs can't read the sections/config
    // tables, must not render field inputs, and must not send custom patches.
    const isFree = isFreeOrg(entitlements);
    const { data: personCustomFieldSections } = Query.personCustomFieldSections.all({ options: { enabled: !isFree } })
    const { data: loggedInUser } = Query.people.loggedInUser()
    const { data: personToEdit } = Query.people.byId({id: id, options: {enabled: !!id}})

    // Editing your own profile. Users without 'Edit People' still get a
    // restricted self-edit path covering their own basic/contact fields.
    const isSelf = !!id && id === loggedInUser?.id;
    const selfEdit = isSelf && !isBatch && !loggedInUser?.can?.('Edit People') && !loggedInUser?.isAdmin;

    useEffect(() => {
        if(!isBatch && personToEdit && !Object.keys(personDetails).length){
            setEditScreenData("AddPersonModal", storeKey, {
                ...personToEdit,
                Roles: personToEdit?.Roles?.filter(r => !r.isDynamicRole),
                originalValues: personToEdit,
            })
        }
    }, [personToEdit])
    
    
    const setPersonDetails = (details) => {
        setEditScreenData("AddPersonModal", storeKey, details)
    }

    const saveUserSettingsMutation = useMutation({ mutationFn: saveUserSettings });

    const savePersonMutation = Mutation.people.save({
        onSuccess: async (data, variables) => {
            removeEditScreenData("AddPersonModal", storeKey)
            
            if(data.id == loggedInUser?.id){
                queryClient.invalidateQueries({queryKey: QueryKeys.people.loggedInUser()});
            }

            // Send opt-in messages for phones that just changed from never_opted_in to opted_in
            const originalPhones = personToEdit?.Phones || [];
            const newPhones = variables.Phones || [];
            const newlyOptedIn = newPhones.filter(np => {
                if (np.smsOptInStatus !== 'opted_in') return false;
                const orig = originalPhones.find(op => op.phoneNumber.trim() === np.phoneNumber.trim());
                return !orig || orig.smsOptInStatus === 'never_opted_in';
            });
            for (const phone of newlyOptedIn) {
                const orig = originalPhones.find(op => op.phoneNumber.trim() === phone.phoneNumber.trim());
                sendOptIn({ personID: data.id, phoneNumber: phone.phoneNumber, previousStatus: orig?.smsOptInStatus || 'never_opted_in' }).catch(() => {});
            }

            if(fromScreen){
                // SheetSelector "Add Person" contract — same as addresses/create etc.:
                // hand the new person to the selector's onChange, then pop back.
                useEditScreenStore.getState().dataChangeFunction?.(data)
                router.dismissTo({pathname: fromScreen, params: fromStoreKey && fromStoreKey != "new" ? {id: fromStoreKey} : undefined});
            }
            else if(from){
                setEditScreenData(from, fromStoreKey, data)
                const familyMemberIDs = variables?.Family?.FamilyMembers?.map(m => m.id).filter(Boolean) || [];
                router.replace({pathname: from, params: {newPersonID: data.id, newFamilyMemberIDs: familyMemberIDs.join(',')}})
            }
            else{
                redirectToDefault(router, "AddPersonModal")
            }
        }
    })

    const bulkUpdateMutation = Mutation.people.bulkUpdate({
        onSuccess: () => {
            removeEditScreenData("AddPersonModal", storeKey)
            Toast.show({ type: 'success', text2: `${batchIdList.length} ${batchIdList.length === 1 ? 'person' : 'people'} updated.` });
            router.back();
        }
    })

    const isEmptyValue = (val) => val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0);

    const handleBatchSave = () => {
        const scalarPatch = {};
        if(!isEmptyValue(personDetails.gender)) scalarPatch.gender = personDetails.gender;
        if(!isEmptyValue(personDetails.dateOfBirth)){
            scalarPatch.dateOfBirth = typeof personDetails.dateOfBirth == 'string'
                ? personDetails.dateOfBirth
                : personDetails.dateOfBirth?.toLocaleDateString();
        }
        if(!isEmptyValue(personDetails.invitedByID)) scalarPatch.invitedByID = personDetails.invitedByID;
        if(!isEmptyValue(personDetails.invitedByNote)) scalarPatch.invitedByNote = personDetails.invitedByNote.trim();

        const fileCustomFieldIds = new Set(
            (personCustomFieldSections || []).flatMap(s => s.CustomFields || []).filter(f => f.type === 'File Upload').map(f => f.id)
        );
        const customPatch = {};
        // Free orgs must never send custom-field updates — bulk_update_people
        // rejects them server-side; skip even if stale store data exists.
        if(!isFree){
            Object.entries(personDetails.customFieldValues || {}).forEach(([fieldID, value]) => {
                if(!isEmptyValue(value) && !fileCustomFieldIds.has(fieldID)) customPatch[fieldID] = value;
            });
        }

        if(Object.keys(scalarPatch).length === 0 && Object.keys(customPatch).length === 0){
            Toast.show({ type: 'error', text2: 'No changes to save' });
            return true;
        }

        bulkUpdateMutation.mutate({ personIds: batchIdList, scalarPatch, customPatch });
    }

    useEffect(() => {
        if(firstRender){
            setTimeout(() => {
                setFirstRender(false)  
            }, 100)
        }
    }, [])


    const formatPhoneNumber = (number) => {
        const input = number.replace(/\D/g,'').substring(0,10);
        const areaCode = input.substring(0,3);
        const middle = input.substring(3,6);
        const last = input.substring(6,10);
    
        if(input.length > 6){number = `(${areaCode}) ${middle}-${last}`;}
        else if(input.length > 3){number = `(${areaCode}) ${middle}`;}
        else if(input.length > 0){number = `(${areaCode}`;}

        return number;
    };

    const updatePhone = (i, field, value) => {
        const phones = [...(personDetails.Phones || [])];
        phones[i] = { ...phones[i], [field]: value };
        setPersonDetails({ Phones: phones });
    };

    const updatePhoneSMS = (i, enabled) => {
        const phones = [...(personDetails.Phones || [])];
        if (enabled) {
            phones.forEach((p, idx) => {
                if (idx === i) {
                    phones[idx] = { ...p, smsOptInStatus: 'opted_in' };
                } else if (p.smsOptInStatus === 'opted_in') {
                    phones[idx] = { ...p, smsOptInStatus: 'opt_in_paused' };
                }
            });
        } else {
            const currentStatus = phones[i].smsOptInStatus;
            const newStatus = currentStatus === 'opted_in' ? 'opt_in_paused' : currentStatus;
            phones[i] = { ...phones[i], smsOptInStatus: newStatus };
        }
        setPersonDetails({ Phones: phones });
    };

    const removePhone = (i) => {
        const phones = [...(personDetails.Phones || [])];
        phones.splice(i, 1);
        setPersonDetails({ Phones: phones });
    };

    const addPhone = () => {
        const phones = [...(personDetails.Phones || [])];
        phones.push({ phoneNumber: '', label: '', smsOptInStatus: 'never_opted_in', private: false });
        setPersonDetails({ Phones: phones });
    };

    const PHONE_LABEL_OPTIONS = ['Mobile', 'Home', 'Work', 'Other'];

    const phone_number_inputs = (personDetails.Phones || []).map((phone, i) => (
        <View key={`phone_${i}`} style={styles.phoneCard}>
            <View style={styles.phoneCardHeader}>
                <Text style={styles.phoneCardTitle}>{i === 0 ? 'Primary Phone' : `Phone ${i + 1}`}</Text>
                <TouchableOpacity onPress={() => removePhone(i)} style={styles.phoneRemoveBtn}>
                    <Unicon name="remove-circle" size={20} colorVariant="error" />
                </TouchableOpacity>
            </View>

            <Input
                title="Phone Number"
                value={phone.phoneNumber}
                maxLength={14}
                type="phone-pad"
                onUpdate={(val) => updatePhone(i, 'phoneNumber', formatPhoneNumber(val))}
            />

            <View style={styles.phoneLabelRow}>
                {PHONE_LABEL_OPTIONS.map(opt => (
                    <Pressable
                        key={opt}
                        onPress={() => updatePhone(i, 'label', phone.label === opt ? '' : opt)}
                        style={[styles.phoneLabelChip, phone.label === opt && styles.phoneLabelChipActive]}
                    >
                        <Text style={[styles.phoneLabelChipText, phone.label === opt && styles.phoneLabelChipTextActive]}>{opt}</Text>
                    </Pressable>
                ))}
            </View>

            {phone.label === 'Other' && (
                    <Input
                        placeholder="Custom Label"
                        value={phone.customLabel || ''}
                        onUpdate={(val) => updatePhone(i, 'customLabel', val)}
                        style={{ marginTop: 8 }}
                    />
                )}

            {phone.smsOptInStatus === 'opted_out' && (
                <View style={styles.phoneOptInRow}>
                    <Text style={[styles.phoneDisclaimerText, { color: '#d97706' }]}>
                        <Unicon name="warning-outline" size={12} colorVariant="warning" /> This number has opted out of SMS. Reply START to {phone.phoneNumber || 'this number'} to re-enable.
                    </Text>
                </View>
            )}
            {phone.smsOptInStatus === 'non_mobile' && (
                <View style={styles.phoneOptInRow}>
                    <Text style={[styles.phoneDisclaimerText, { color: '#6b7280' }]}>
                        <Unicon name="phone-off" library="material" size={12} colorVariant="textSecondary" /> This number is not a mobile phone and cannot receive SMS messages.
                    </Text>
                </View>
            )}
            {phone.smsOptInStatus === 'opt_in_paused' && (
                <View style={styles.phoneOptInRow}>
                    <Text style={[styles.phoneDisclaimerText, { color: '#6b7280' }]}>
                        <Unicon name="pause-circle-outline" size={12} colorVariant="textSecondary" /> SMS is paused for this number. Check the box to re-enable without sending another opt-in message.
                    </Text>
                </View>
            )}
            <View style={styles.phoneToggleRow}>
                <View style={styles.phoneToggleItem}>
                    <Checkbox
                        checked={phone.smsOptInStatus === 'opted_in'}
                        onChange={(val) => updatePhoneSMS(i, val)}
                        disabled={phone.smsOptInStatus === 'opted_out' || phone.smsOptInStatus === 'non_mobile'}
                    />
                    <View style={styles.phoneToggleText}>
                        <Unicon name="chatbubble-ellipses-outline" size={16} colorVariant="textSecondary" />
                        <Text style={styles.phoneToggleLabel}>Use this number for SMS communications</Text>
                    </View>
                </View>
            </View>
            {phone.smsOptInStatus === 'opted_in' && (
                <View style={styles.phoneOptInRow}>
                    <Text style={styles.phoneDisclaimerText}>
                        {personDetails.id === loggedInUser?.id ? (
                            <>By checking this box, you agree to receive recurring automated text messages from Reach at {phone.phoneNumber || 'this number'}. </>
                        ) : (
                            <>By checking this box, you agree that {personDetails.firstName} {personDetails.lastName} has consented to receive recurring automated text messages from Reach at {phone.phoneNumber || 'this number'}. </>
                        )}
                        Consent is not a condition of purchase. Msg & data rates may apply. Reply STOP to cancel. Reply HELP for help. View our{' '}
                        <Text style={styles.phoneDisclaimerLink} onPress={() => Linking.openURL('https://www.reachchms.com/privacy-policy')}>Privacy Policy</Text>
                        {' '}and{' '}
                        <Text style={styles.phoneDisclaimerLink} onPress={() => Linking.openURL('https://www.reachchms.com/terms')}>Terms of Service</Text>
                        .
                    </Text>
                </View>
            )}
            <View style={styles.phoneToggleRow}>
                <View style={styles.phoneToggleItem}>
                    <Checkbox
                        checked={!!phone.private}
                        onChange={(val) => updatePhone(i, 'private', val)}
                    />
                    <View style={styles.phoneToggleText}>
                        <Unicon name="lock-closed-outline" size={16} colorVariant="textSecondary" />
                        <Text style={styles.phoneToggleLabel}>Private</Text>
                    </View>
                </View>
            </View>
        </View>
    ));


    const RelationshipInput = (person) => {
        const onSelect = (new_value) => {
            const isAnotherFamilyMemberHeadOfHousehold = new_value == "Head of Household" && personDetails.Family?.FamilyMembers?.findIndex(n => n.id != person.Family?.FamilyMembers?.find(fm => fm.id == person.Family?.FamilyMembers?.find(fm2 => fm2.id == person.Family?.FamilyMember.id)?.id)?.id && n.familyRole == "Head of Household") > -1;

            if(isAnotherFamilyMemberHeadOfHousehold){
                Toast.show({type: 'error', text2: "You cannot have more than one Head of Household in a family."})
                return {...personDetails}
            }
            
            const newFamily = [...(personDetails.Family?.FamilyMembers || [])];
            const currentFamilyIndex = newFamily.findIndex(n => n.id == person.familyMember.id);

            newFamily[currentFamilyIndex].familyRole = new_value;

            setPersonDetails({
                Family: {
                    ...personDetails.Family,
                    FamilyMembers: newFamily
                }
            })
        }

        return <DropdownMenu testID="Family Position" triggerComponent={<FamilyPositionTrigger role={person.familyMember.familyRole} />} options={[
            {name: "Head of Household", onSelect: (option) => onSelect(option.name)},
            {name: "Spouse", onSelect: (option) => onSelect(option.name)},
            {name: "Adult", onSelect: (option) => onSelect(option.name)},
            {name: "Child", onSelect: (option) => onSelect(option.name)},
            {name: "Unassigned", onSelect: (option) => onSelect(option.name)},
        ]}/>
    }


    //Build out custom field sections based on the info this person has on their profile
    const customFieldSectionComponents = useMemo(() => {
        return (!isFree && personCustomFieldSections) ? personCustomFieldSections?.map(s => {
            const fields = s.CustomFields?.filter(field => !isBatch || field.type !== 'File Upload')?.map(field => {
                return <CustomField
                        key={field.id}
                        fieldType={field.type}
                        required={isBatch ? false : field.required}
                        onUpdate={(new_value) => {
                            let old_custom_values = personDetails.customFieldValues ? personDetails.customFieldValues : {}

                            setPersonDetails({
                                customFieldValues: {
                                    ...old_custom_values,
                                    [field.id]: new_value
                                }
                            })
                        }}
                        options={{
                            title: field.name,
                            value: personDetails.customFieldValues?.[field.id],
                            options: field.options
                        }}
                    />
            })

            if(!fields || fields.length == 0 ){
                return null
            }
            else{
                return {key: s.name, component: () => (<View key={s.name} style={{flex:1}}>
                    <KeyboardAwareScrollView keyboardShouldPersistTaps="handled" bottomOffset={100} style={{flex:1}}>
                        <InputSection style={{marginHorizontal:5}} title={s.name}>
                            {fields}
                        </InputSection>
                    </KeyboardAwareScrollView>
                </View>)}
            }

        }).filter(s => s !== null) : [];
    }, [personCustomFieldSections, personDetails.customFieldValues, setPersonDetails, isBatch, isFree]);

    const ProfileSection = () => (
        <View key="profile" style={{flex:1}}>
            <KeyboardAwareScrollView keyboardShouldPersistTaps="handled" bottomOffset={100} style={{flex:1}}>
            <InputSection style={{marginHorizontal:5}} title="Profile Info" description="Basic Member Information">

                {visibleDuplicates.length > 0 && (
                    <View style={styles.duplicateWarningContainer} testID="DuplicateWarning">
                        <View style={styles.duplicateWarningHeader}>
                            <View style={styles.duplicateWarningTitleRow}>
                                <Unicon name="warning-outline" size={18} colorVariant="warning" />
                                <Text style={styles.duplicateWarningTitle}>
                                    Possible duplicate{visibleDuplicates.length > 1 ? 's' : ''} ({visibleDuplicates.length})
                                </Text>
                            </View>
                            <TouchableOpacity
                                testID="DuplicateWarningIgnore"
                                onPress={() => setIgnoredDuplicateKey(duplicateKey)}
                                style={styles.duplicateWarningIgnoreBtn}
                            >
                                <Unicon name="close" size={16} colorVariant="textSecondary" />
                                <Text style={styles.duplicateWarningIgnoreText}>Ignore</Text>
                            </TouchableOpacity>
                        </View>
                        <Text style={styles.duplicateWarningSubtitle}>
                            Someone with this name already exists. Review to make sure you aren&apos;t creating a duplicate.
                        </Text>
                        {visibleDuplicates.map((person) => (
                            <ProfileCard
                                key={person.id}
                                profile={person}
                                subtitle={person.email || (person.dateOfBirth ? `Born ${new Date(person.dateOfBirth).toLocaleDateString()}` : null)}
                                backgroundColorVariant="background"
                                testID={`DuplicateWarning-Person-${person.id}`}
                                onPress={() => setReviewPersonId(person.id)}
                            />
                        ))}
                    </View>
                )}

                <View style={{ flexDirection:"row", gap:10, justifyContent:"space-between", paddingTop:10}}>
                    <View style={{flex:1}}>
                        <Input
                            required
                            disabled={isBatch}
                            title="First Name"
                            autoComplete='given-name'
                            value={personDetails.firstName}
                            onUpdate={(new_value) => {
                                const familyMemberForThisPerson = id == undefined 
                                    ? personDetails.Family?.FamilyMembers?.find(fm => !fm.id)
                                    : personDetails.Family?.FamilyMembers?.find(fm => fm.id == id)

                                const existingFamilyMembers = personDetails.Family?.FamilyMembers?.filter(fm => fm.id && fm.id != familyMemberForThisPerson?.id)

                                setPersonDetails({
                                    firstName: new_value,
                                    Family: familyMemberForThisPerson ? {...personDetails.Family, FamilyMembers: [{...familyMemberForThisPerson, firstName: new_value},...existingFamilyMembers]} : personDetails.Family
                                })
                            }}
                        />
                    </View>

                    <View style={{flex:1}}>
                        <Input
                            required
                            disabled={isBatch}
                            title="Last Name"
                            autoComplete='family-name'
                            value={personDetails.lastName}
                            onUpdate={(new_value) => {
                                const familyMemberForThisPerson = id == undefined 
                                    ? personDetails.Family?.FamilyMembers?.find(fm => !fm.id)
                                    : personDetails.Family?.FamilyMembers?.find(fm => fm.id == id)

                                const existingFamilyMembers = personDetails.Family?.FamilyMembers?.filter(fm => fm.id && fm.id != familyMemberForThisPerson?.id)

                                setPersonDetails({
                                    lastName: new_value,
                                    Family: familyMemberForThisPerson ? {...personDetails.Family, FamilyMembers: [{...familyMemberForThisPerson, lastName: new_value},...existingFamilyMembers]} : personDetails.Family
                                })
                            }}
                        />
                    </View>
                </View>

                {(isSelf || loggedInUser?.can?.('View Basic Info')) && (
                    <>
                        <SelectInput
                            title="Gender"
                            value={personDetails.gender}
                            options={["Male", "Female"]}
                            onUpdate={(new_value) => {
                                setPersonDetails({
                                    gender: new_value,
                                })
                            }}
                        />

                        <DatePickerInput
                            title="Date of Birth"
                            mode={"date"}
                            value={personDetails.dateOfBirth}
                            onUpdate={(new_value) => {
                                setPersonDetails({
                                    dateOfBirth: new_value
                                })
                            }}
                        />
                    </>
                )}

                {loggedInUser?.can?.('View Basic Info') && (
                    <>
                        <SheetSelector type="people" title="Invited By" value={personDetails.InvitedBy} onChange={(selected_item) => {
                            setPersonDetails({
                                invitedByID: selected_item?.id,
                                InvitedBy: selected_item
                            })
                        }} />

                        <Input
                            title="Invited By Note"
                            placeholder="Add a note about who invited this person..."
                            value={personDetails.invitedByNote || ''}
                            onUpdate={(text) => setPersonDetails({ invitedByNote: text })}
                        />
                    </>
                )}
            </InputSection>
            </KeyboardAwareScrollView>
        </View>
    )


    const ContactSection = () => (
        <View key="contact" style={{flex:1}}>
            <KeyboardAwareScrollView keyboardShouldPersistTaps="handled" bottomOffset={100} style={{flex:1}}>
            <InputSection style={{marginHorizontal:5}} title="Contact Info" description="Contact information for the member">
                {!isBatch && phone_number_inputs}

                {!isBatch && (
                    <TouchableOpacity style={styles.addPhoneBtn} onPress={addPhone}>
                        <Unicon name="add-circle-outline" size={18} colorVariant="primary" />
                        <Text style={styles.addPhone}> Add Phone Number</Text>
                    </TouchableOpacity>
                )}


                <Input
                    required
                    disabled={isBatch}
                    title="Email"
                    autoCapitalize='none'
                    type="email-address"
                    autoComplete="email"
                    value={personDetails.email}
                    onUpdate={(new_value) => {
                        setPersonDetails({
                            email: new_value
                        })
                    }}
                />

                <SheetSelector type="addresses" title="Address" disabled={isBatch} fromScreen={pathname} fromStoreKey={storeKey} addScreenName="addresses/create" value={personDetails.Address} onChange={(selected_item) => {
                    setPersonDetails({
                        addressID: selected_item?.id,
                        Address: selected_item
                    })
                }} />
            </InputSection>
            </KeyboardAwareScrollView>
        </View>
    )

    const isThisPerson = (member) => {
        if (id) return member.id === id;
        return !member.id;
    };

    const handleJoinExistingFamily = async (selectedPerson) => {
        const fullPerson = await getPersonProfile(selectedPerson.id);
        
        if (!fullPerson || !fullPerson?.Family?.FamilyMembers) {
            Toast.show({ type: 'error', text2: 'This person is not part of a family. Select someone who belongs to a family.' });
            return;
        }

        const thisPerson = {
            id: id || undefined,
            firstName: personDetails.firstName || 'New Person',
            lastName: personDetails.lastName || '',
            familyRole: 'Unassigned'
        };

        setPersonDetails({
            Family: {
                ...fullPerson.Family,
                FamilyMembers: [...fullPerson.Family.FamilyMembers, thisPerson]
            }
        });
    };

    const handleQuickAddFamilyMember = (newPerson) => {
        setShowQuickAddFamily(false);
        
        const newMember = {
            id: newPerson.id,
            firstName: newPerson.firstName,
            lastName: newPerson.lastName,
            familyRole: 'Unassigned'
        };

        if (personDetails.Family) {
            setPersonDetails({
                Family: {
                    ...personDetails.Family,
                    FamilyMembers: [...(personDetails.Family.FamilyMembers || []), newMember]
                }
            });
        } else {
            const thisPerson = {
                id: id || undefined,
                firstName: personDetails.firstName || 'New Person',
                lastName: personDetails.lastName || '',
                familyRole: 'Head of Household'
            };
            setPersonDetails({
                Family: {
                    FamilyMembers: [thisPerson, newMember]
                }
            });
        }
    };

    const FamilySection = () => (
        <View key="family" style={{flex:1}}>
            <KeyboardAwareScrollView keyboardShouldPersistTaps="handled" bottomOffset={100} style={{flex:1}}>
            <InputSection style={{marginHorizontal:5}} title="Family" description="Add this person to a family or create a new one">
                {!personDetails.Family && familyMode === null ? (
                    <View style={styles.familyOptionsContainer}>
                        <Text style={styles.familyHelpText}>
                            This person is not currently part of a family. Choose an option below:
                        </Text>

                        <Pressable 
                            testID="Family-Create"
                            accessibilityRole="button"
                            style={styles.familyOptionCard}
                            onPress={() => {
                                const thisPerson = {
                                    id: id || undefined,
                                    firstName: personDetails.firstName || 'New Person',
                                    lastName: personDetails.lastName || '',
                                    familyRole: 'Head of Household'
                                };
                                setPersonDetails({
                                    Family: { FamilyMembers: [thisPerson] }
                                });
                            }}
                        >
                            <View style={styles.familyOptionIcon}>
                                <Unicon name="home-outline" size={22} colorVariant="people" />
                            </View>
                            <View style={styles.familyOptionContent}>
                                <Text style={styles.familyOptionTitle}>Create New Family</Text>
                                <Text style={styles.familyOptionDescription}>
                                    Start a new family with this person as the head of household
                                </Text>
                            </View>
                            <Unicon name="chevron-forward" size={20} colorVariant="textSecondary" />
                        </Pressable>

                        <Pressable 
                            testID="Family-Join"
                            accessibilityRole="button"
                            style={styles.familyOptionCard}
                            onPress={() => setFamilyMode('join')}
                        >
                            <View style={styles.familyOptionIcon}>
                                <Unicon name="people-outline" size={22} colorVariant="people" />
                            </View>
                            <View style={styles.familyOptionContent}>
                                <Text style={styles.familyOptionTitle}>Join Existing Family</Text>
                                <Text style={styles.familyOptionDescription}>
                                    Add this person to an existing family by selecting a current member
                                </Text>
                            </View>
                            <Unicon name="chevron-forward" size={20} colorVariant="textSecondary" />
                        </Pressable>
                    </View>
                ) : !personDetails.Family && familyMode === 'join' ? (
                    <View style={styles.familyOptionsContainer}>
                        <View style={styles.familyMembersHeader}>
                            <Text style={styles.familyMembersTitle}>Join Existing Family</Text>
                            <SolidButton 
                                text="Cancel" 
                                type="secondary" 
                                variant="outlined" 
                                size="sm" 
                                onPress={() => setFamilyMode(null)}
                            />
                        </View>
                        <Text style={styles.familyHelpText}>
                            Select a person who is already in a family. This person will be added to their family.
                        </Text>
                        <SheetSelector
                            type="people"
                            title="Select Family Member"
                            placeholder="Search for a person in a family..."
                            excludeIds={id ? [id] : []}
                            errorFn={(person) => !person.familyID ? ["Not in a family"] : null}
                            disableErroredOptions
                            onChange={(selectedPerson) => {
                                handleJoinExistingFamily(selectedPerson);
                                setFamilyMode(null);
                            }}
                        />
                    </View>
                ) : (
                    <View>
                        <View style={styles.familyMembersHeader}>
                            <View style={styles.familyTitleRow}>
                                <Unicon name="people-outline" size={18} colorVariant="people" />
                                <Text style={styles.familyMembersTitle}>Family Members</Text>
                                <Text style={styles.familyMemberCount}>{personDetails.Family?.FamilyMembers?.length || 0}</Text>
                            </View>
                            <SolidButton 
                                text="Leave Family"
                                testID="Family-Leave"
                                type="error"
                                variant="muted"
                                size="sm" 
                                onPress={() => setPersonDetails({ Family: undefined })}
                            />
                        </View>

                        <View style={styles.familyMemberList}>
                            {personDetails.Family?.FamilyMembers?.map((familyMember, idx) => (
                                <FamilyMemberRow
                                    key={familyMember.id || idx}
                                    profile={familyMember}
                                    testID={`FamilyMember-${familyMember.id || idx}`}
                                    removeDisabled={isThisPerson(familyMember)}
                                    isCurrentPerson={isThisPerson(familyMember)}
                                    isLast={idx === personDetails.Family.FamilyMembers.length - 1}
                                    relationshipInput={<RelationshipInput familyMember={familyMember} />}
                                    onRemove={() => {
                                        const remaining = personDetails.Family?.FamilyMembers?.filter((_, i) => i !== idx);
                                        if (remaining.length === 0) {
                                            setPersonDetails({ Family: undefined });
                                        } else {
                                            setPersonDetails({
                                                Family: { ...personDetails.Family, FamilyMembers: remaining }
                                            });
                                        }
                                    }}
                                />
                            ))}
                        </View>

                        <View style={styles.addFamilyMemberActions}>
                            <Text style={styles.familyMembersTitle}>Add a family member</Text>
                            <SheetSelector 
                                type="people" 
                                title="Add Existing Person"
                                placeholder="Search for a person..."
                                excludeIds={personDetails.Family?.FamilyMembers?.map(fm => fm.id).filter(Boolean)}
                                errorFn={(person) => person.Family && person.Family.id !== personDetails.Family?.id ? ["Already in another family"] : null}
                                disableErroredOptions
                                onChange={(selectedPerson) => {
                                    const newMember = {
                                        id: selectedPerson.id,
                                        firstName: selectedPerson.firstName,
                                        lastName: selectedPerson.lastName,
                                        familyRole: 'Unassigned'
                                    };
                                    setPersonDetails({
                                        Family: {
                                            ...personDetails.Family,
                                            FamilyMembers: [...(personDetails.Family?.FamilyMembers || []), newMember]
                                        }
                                    });
                                }}
                            />

                            <View>
                                <View style={styles.separator}></View>
                                <View style={{alignItems: 'center'}}><Text style={styles.separatorText}>OR</Text></View>
                            </View>

                            <View>
                                <SolidButton 
                                    text="Quick Add New Person"
                                    testID="Family-QuickAdd"
                                    type="people"
                                    variant="muted"
                                    size="md"
                                    iconName="add-circle-outline" 
                                    iconBefore
                                    onPress={() => setShowQuickAddFamily(true)}
                                />
                            </View>
                        </View>
                    </View>
                )}
            </InputSection>

            <SheetModal
                isOpen={showQuickAddFamily}
                onClose={() => setShowQuickAddFamily(false)}
                title="Add Family Member"
                stayOpenOnBlur
            >
                <AddPersonQuickForm
                    fromScreen={pathname}
                    fromStoreKey={storeKey}
                    formKey={`family-${storeKey}`}
                    familySource={{ ...personDetails, id: id || undefined }}
                    onSuccess={handleQuickAddFamilyMember}
                    buttonText="Add to Family"
                    showCustomizeButton={false}
                    showAddAnother={false}
                    showFamily={false}
                />
            </SheetModal>
            </KeyboardAwareScrollView>
        </View>
    )

    const RolesSection = () => (
        <View key="roles" style={{flex:1}}>
            <KeyboardAwareScrollView keyboardShouldPersistTaps="handled" bottomOffset={100} style={{flex:1}}>
            <InputSection style={{marginHorizontal:5}} title="Roles" description="Add roles to easily categorize this person for permissions and categorization">
                <SheetSelector
                    multiple
                    type="roles"
                    title="Roles"
                    fromScreen={`people/create`}
                    fromStoreKey={storeKey}
                    value={personDetails.Roles || []}
                    onChange={(selected_items) => {
                        setPersonDetails({
                            Roles: selected_items
                        })
                    }}
                    errorFn={(role) => role.hasLockedPermissions ? ["Contains locked permissions"] : (role.quickFilterID ? ["Dynamic role — members are set by its filter"] : null)}
                    disableErroredOptions
                />
            </InputSection>
            </KeyboardAwareScrollView>
        </View>
    )

    const profileSectionRef = useRef(ProfileSection);
    profileSectionRef.current = ProfileSection;
    
    const contactSectionRef = useRef(ContactSection);
    contactSectionRef.current = ContactSection;
    
    const familySectionRef = useRef(FamilySection);
    familySectionRef.current = FamilySection;
    
    const rolesSectionRef = useRef(RolesSection);
    rolesSectionRef.current = RolesSection;

    const customFieldSectionComponentsRef = useRef(customFieldSectionComponents);
    customFieldSectionComponentsRef.current = customFieldSectionComponents;

    const renderScene = useCallback(({ route }) => {
        switch (route.key) {
            case 'Profile':
                return profileSectionRef.current();
            case 'Contact':
                return contactSectionRef.current();
            case 'Family':
                return familySectionRef.current();
            case 'Roles':
                return rolesSectionRef.current();
            default: {
                // Handle custom field sections
                const customSection = customFieldSectionComponentsRef.current.find(s => s.key === route.key);
                return customSection ? customSection.component() : null;
            }
        }
    }, []);

    const routes = useMemo(() => {
        const baseRoutes = [
            {key: "Profile", title: "Profile"}
        ];
        if (isSelf || loggedInUser?.can?.('View Contact Info')) {
            baseRoutes.push({key: "Contact", title: "Contact"});
        }
        if (!isBatch && loggedInUser?.can?.('View Basic Info')) {
            baseRoutes.push({key: "Family", title: "Family"});
        }
        if (!isBatch && loggedInUser?.isAdmin) {
            baseRoutes.push({key: "Roles", title: "Roles"});
        }
        if (!isFree && loggedInUser?.can?.('View Custom Fields')) {
            return [
                ...baseRoutes,
                ...customFieldSectionComponents.map(s => ({key: s.key, title: s.key}))
            ];
        }
        return baseRoutes;
    }, [loggedInUser, customFieldSectionComponents, isBatch, isSelf, isFree]);

    // Clamp index to valid range — routes can shrink when permissions load,
    // and react-native-tab-view crashes if index >= routes.length
    useEffect(() => {
        if (index >= routes.length) {
            setIndex(Math.max(0, routes.length - 1));
        }
    }, [routes, index]);

    return (
        <ModalWrapper
            noPadding
            title={isBatch ? "Batch Edit" : (isSelf ? "Edit My Profile" : (id ? "Edit Person" : "New Person"))}
            subtitle={isBatch ? `${batchIdList.length} ${batchIdList.length === 1 ? 'Person' : 'People'}` : undefined}
            loading={savePersonMutation.isPending || bulkUpdateMutation.isPending}
            onCancel={() => {
                removeEditScreenData("AddPersonModal", storeKey)
            }}
            onConfirm={() => {
                if(isBatch){
                    return handleBatchSave();
                }

                let newPerson = { ...personDetails };

                if(!Validator.areStringsPopulated([newPerson.firstName, newPerson.lastName])){
                    Toast.show({type:"error", text2: "A new person must have a first & last name"});
                    return true;
                }

                if(!selfEdit){
                    const missingRequiredFields = (personCustomFieldSections || []).flatMap(s => s.CustomFields || []).filter(f => f.required && !f.isDeleted);
                    const missingField = missingRequiredFields.find(f => {
                        const val = newPerson.customFieldValues?.[f.id];
                        return val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0);
                    });
                    if(missingField){
                        Toast.show({type:"error", text2: `Please fill out the required field: ${missingField.name}`});
                        return true;
                    }
                }

                if(newPerson.email && !Validator.validateEmail(newPerson.email)){
                    Toast.show({type:"error", text2: "Email address is not a valid email!"});
                    return true;
                }

                const nameRegex = /^[\p{L}][\p{L}\p{N} \-`']*$/u;
                if (!nameRegex.test(newPerson.firstName) || !nameRegex.test(newPerson.lastName)) {
                    Toast.show({type:"error", text2: "First or last name contains invalid characters. Please remove any special characters or numbers"});
                    return true;
                }

                if (newPerson.Family?.FamilyMembers?.length > 0) {
                    const thisMember = id
                        ? newPerson.Family.FamilyMembers.find(fm => fm.id === id)
                        : newPerson.Family.FamilyMembers.find(fm => !fm.id);

                    newPerson = {
                        ...newPerson,
                        familyRole: thisMember?.familyRole || 'Unassigned',
                        Family: {
                            ...newPerson.Family,
                            FamilyMembers: newPerson.Family.FamilyMembers
                                .filter(fm => fm.id && fm.id !== id)
                                .map(fm => ({
                                    id: fm.id,
                                    familyRole: fm.familyRole
                                }))
                        }
                    };
                }

                if(personDetails.id === loggedInUser?.id){
                    const hasSmsPhone = (personDetails.Phones || []).some(p => p.smsOptInStatus === 'opted_in');
                    saveUserSettingsMutation.mutate({ textMessagingOptIn: hasSmsPhone });
                }

                if(selfEdit){
                    // Strip everything outside the whitelisted self-edit columns
                    // so nothing restricted reaches savePerson.
                    newPerson = {
                        ...Object.fromEntries(Object.entries(newPerson).filter(([key]) => !SELF_EDIT_BLOCKED_KEYS.includes(key))),
                        selfEdit: true,
                    };
                }

                const { originalValues, ...detailsWithoutOriginal } = newPerson;
                savePersonMutation.mutate({ ...detailsWithoutOriginal, originalValues });
            
            }}
        >
            <View style={{flex:1}}>
                <TabView
                    swipeEnabled={isNative}
                    renderTabBar={renderTabBar}
                    navigationState={{ index, routes }}
                    renderScene={renderScene}
                    onIndexChange={setIndex}
                    lazy
                    lazyPreloadDistance={0}
                />
            </View>

            <DuplicateReviewModal
                personId={reviewPersonId}
                onClose={() => setReviewPersonId(null)}
            />
        </ModalWrapper>
    );
}


const styles = StyleSheet.create(({colors, fonts}) => {
    return {
        duplicateWarningContainer: {
            borderWidth: 1,
            borderColor: colors.warning,
            borderRadius: 12,
            backgroundColor: colors.warningContainer || colors.surface,
            padding: 12,
            marginTop: 10,
            gap: 8,
        },
        duplicateWarningHeader: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        duplicateWarningTitleRow: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
        },
        duplicateWarningTitle: {
            fontFamily: fonts.bold,
            fontSize: 14,
            color: colors.warning,
        },
        duplicateWarningSubtitle: {
            fontSize: 12,
            fontFamily: fonts.body,
            color: colors.textSecondary,
            lineHeight: 16,
        },
        duplicateWarningIgnoreBtn: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 4,
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 8,
            backgroundColor: colors.surface,
        },
        duplicateWarningIgnoreText: {
            fontSize: 12,
            fontFamily: fonts.medium,
            color: colors.textSecondary,
        },
        addPhone: {
            fontFamily: fonts.bold,
            color: colors.primary,
            fontSize: 14,
        },
        addPhoneBtn: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            marginTop: 4,
            marginBottom: 8,
        },
        phoneCard: {
            borderWidth: 1,
            borderColor: colors.border,
            borderRadius: 12,
            padding: 14,
            marginBottom: 12,
            backgroundColor: colors.background,
            gap: 2,
        },
        phoneCardHeader: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 4,
        },
        phoneCardTitle: {
            fontFamily: fonts.bold,
            fontSize: 14,
            color: colors.text,
        },
        phoneRemoveBtn: {
            padding: 2,
        },
        phoneLabelRow: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 8,
            marginTop: 8,
            marginBottom: 4,
        },
        phoneLabelChip: {
            paddingHorizontal: 12,
            paddingVertical: 5,
            borderRadius: 20,
            borderWidth: 1,
            borderColor: colors.border,
            backgroundColor: colors.surface,
        },
        phoneLabelChipActive: {
            backgroundColor: colors.primaryContainer,
            borderColor: colors.primary,
        },
        phoneLabelChipText: {
            fontSize: 12,
            fontFamily: fonts.body,
            color: colors.textSecondary,
        },
        phoneLabelChipTextActive: {
            fontFamily: fonts.bold,
            color: colors.primary,
        },
        phoneToggleRow: {
            flexDirection: 'row',
            marginTop: 10,
            paddingTop: 10,
        },
        phoneToggleItem: {
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 12,
        },
        phoneToggleDivider: {
            width: 1,
            backgroundColor: colors.border,
            marginHorizontal: 12,
        },
        phoneToggleText: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
        },
        phoneToggleLabel: {
            fontSize: 13,
            fontFamily: fonts.body,
            color: colors.textSecondary,
        },
        phoneSwitch: {
            transform: [{ scaleX: 0.85 }, { scaleY: 0.85 }],
        },
        phoneOptInRow: {
            marginTop: 8,
        },
        phoneDisclaimerText: {
            flex: 1,
            fontSize: 11,
            fontFamily: fonts.body,
            color: colors.textTertiary,
            lineHeight: 16,
        },
        phoneDisclaimerLink: {
            fontSize: 11,
            fontFamily: fonts.body,
            color: colors.primary,
        },
        familyOptionsContainer: {
            gap: 12,
            paddingTop: 8,
        },
        familyHelpText: {
            color: colors.textSecondary,
            fontSize: 13,
            lineHeight: 19,
            fontFamily: fonts.body,
        },
        familyOptionCard: {
            flexDirection: 'row',
            alignItems: 'center',
            padding: 16,
            backgroundColor: colors.background,
            borderRadius: 12,
            gap: 12,
            _web: {
                _hover: { backgroundColor: colors.hover },
                '_focus-visible': { outlineColor: colors.people, outlineStyle: 'solid', outlineWidth: 2 },
            },
        },
        familyOptionIcon: {
            width: 40,
            height: 40,
            borderRadius: 12,
            backgroundColor: colors.peopleContainer,
            alignItems: 'center',
            justifyContent: 'center',
        },
        familyOptionContent: {
            flex: 1,
            gap: 4,
        },
        familyOptionTitle: {
            fontSize: 15,
            fontFamily: fonts.heading,
            color: colors.text,
        },
        familyOptionDescription: {
            fontSize: 13,
            lineHeight: 19,
            fontFamily: fonts.body,
            color: colors.textSecondary,
        },
        familyMembersHeader: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 8,
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: 8,
            marginBottom: 12,
        },
        familyTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
        familyMemberCount: {
            fontSize: 12,
            fontFamily: fonts.heading,
            fontVariant: ['tabular-nums'],
            color: colors.textSecondary,
            backgroundColor: colors.background,
            paddingHorizontal: 8,
            paddingVertical: 3,
            borderRadius: 8,
        },
        familyMembersTitle: {
            fontSize: 14,
            fontFamily: fonts.heading,
            color: colors.text,
        },
        familyMemberList: {
            backgroundColor: colors.background,
            borderRadius: 12,
        },
        addFamilyMemberActions: {
            gap: 12,
            padding: 16,
            backgroundColor: colors.background,
            borderRadius: 12,
            marginTop: 16,
        },
        separator: {
            height: 1,
            backgroundColor: colors.borderSubtle,
        },
        separatorText: {
            color: colors.textTertiary,
            fontSize: 11,
            fontFamily: fonts.body,
            textAlign: 'center',
            marginTop: -8,
            paddingHorizontal: 10,
            backgroundColor: colors.background,
        },
    }
})